/**
 * AB#661 — which built page does a theme template render?
 *
 * The preview pane shows one page out of the site's last build, so selecting
 * `templates/member/signup.html` has to move the pane to `/member/signup/`
 * instead of leaving it on the homepage.
 *
 * Every path here is an output path the SSG actually emits — see
 * `source/wpp-core/src/ssg/builder.rs`:
 *   index.html          -> `/`                        (build_index)
 *   404.html            -> `/404.html`                (build_404, output root)
 *   archive.html        -> `/posts/`                  (build_archives)
 *   tags.html / tag.html-> `/tags/` , `/tags/{slug}/` (build_tag_index/_pages)
 *   member/{x}.html     -> `/member/{x}/`             (build_member_pages)
 *   page.html + any other top-level template -> `/{slug}/` of the first page
 *                          that renders with it       (build_pages, `template`)
 *   pages/{slug}.html   -> the page using it, else `/{slug}/` (AB#945)
 *   post.html           -> `/posts/{slug}/`           (build_posts)
 */

/**
 * Pseudo file path for the pinned "Theme sample" entry — not a real theme file,
 * it maps to the canned sample page a PREVIEW build writes (dist-preview only).
 */
export const THEME_SAMPLE_PATH = 'theme-sample';

/** Where that sample page lands in the preview build. */
export const THEME_SAMPLE_PREVIEW_PATH = '/__sample/';

export type PreviewCtx = {
  /**
   * `/{slug}/` of the first published page each template renders — pages carry
   * a `template` field (unset = `page.html`), so `wide.html` resolves too.
   */
  pagePathByTemplate?: Record<string, string>;
  /** Newest published post, `/posts/{slug}/`. */
  postPath?: string | null;
  /**
   * AB#845: `/posts/{slug}/` of a published post with comments switched ON.
   * `partials/comments.html` is `{% if comments_html %}` — on any other post it
   * renders nothing, so the pane would show a post with no discussion in it.
   */
  commentsPostPath?: string | null;
  /** First tag, `/tags/{slug}/`. */
  tagPath?: string | null;
};

/**
 * Preview path for a theme file, or `null` to leave the preview where it is
 * (partials, `base.html`, static assets — none of them are a page).
 *
 * @param path Theme-relative file path, e.g. `templates/member/signup.html`.
 */
export function templatePathToPreviewPath(
  path: string | null | undefined,
  ctx: PreviewCtx = {},
): string | null {
  if (!path) return null;
  if (path === THEME_SAMPLE_PATH) return THEME_SAMPLE_PREVIEW_PATH;
  const rel = path.replace(/^\/+/, '');
  if (!rel.startsWith('templates/')) return null;

  const name = rel.slice('templates/'.length);
  if (!name.endsWith('.html')) return null;
  // AB#845: the one partial that owns a page of its own — the discussion only
  // ever renders on a post, so send the pane there instead of nowhere.
  // ponytail: no `#comments` anchor — `path` is also the mint/skipped-page key,
  // so a fragment would have to be split back out at three call sites.
  if (name === 'partials/comments.html') return ctx.commentsPostPath ?? ctx.postPath ?? '/posts/';
  if (name === 'base.html' || name.startsWith('partials/')) return null;

  switch (name) {
    case 'index.html':
      return '/';
    case '404.html':
      return '/404.html';
    case 'archive.html':
      return '/posts/';
    case 'tags.html':
      return '/tags/';
    case 'tag.html':
      return ctx.tagPath ?? '/tags/';
    case 'post.html':
      return ctx.postPath ?? '/posts/';
    default:
      break;
  }

  // The SSG only emits the five known member pages; an unknown one lands on a
  // path the build has no file for, which the pane reports honestly.
  if (name.startsWith('member/')) {
    return `/member/${name.slice('member/'.length, -'.html'.length)}/`;
  }

  // page.html, wide.html, anything else a page can point its `template` at.
  // AB#945: `pages/<slug>.html` is one page's own template — the page using it,
  // else the page it is named after.
  const own = name.startsWith('pages/') ? `/${name.slice('pages/'.length, -'.html'.length)}/` : '/';
  return ctx.pagePathByTemplate?.[name] ?? own;
}

/**
 * The skip entry for the page the pane is showing, if a lenient preview build
 * left it out.
 *
 * ponytail: trailing-slash-tolerant — our mapped paths and the builder's output
 * paths agree today, but a `/x` vs `/x/` drift would otherwise silently show an
 * empty pane instead of the "not renderable" reason.
 */
export function findSkipped<T extends { path: string }>(
  skipped: readonly T[],
  path: string,
): T | undefined {
  const norm = (p: string) => p.replace(/\/+$/, '') || '/';
  return skipped.find((s) => norm(s.path) === norm(path));
}


/** Stay inside one navigable capability when the selected template changes. */
export function builtPreviewUrl(siteSlug: string, capability: { key: string; url: string }, path: string): string | null {
  const parts = path.replace(/^\/+/, '').split('/');
  if (parts.some((part) => part === '.' || part === '..')) return null;
  const base = new URL(`/preview/${encodeURIComponent(siteSlug)}/${encodeURIComponent(capability.key)}/`, capability.url);
  return new URL(parts.map(encodeURIComponent).join('/'), base).href;
}
